import { select, fork, takeLatest, put, call } from 'redux-saga/effects';
import { getErrorMessageKey } from '../utils/helper';
import { USER } from '../actions/user';
import { I18N } from '../actions/i18n';
import {
  login,
  register,
  logout,
  resetPassword,
  getMe,
  updateMe,
  uploadUserImage,
  removeUserImage,
  getUserSettings,
  updateUserSettings,
  getMenuCounters,
  getTranslations,
} from "./../utils/Api";

function* sagaLogin({ email, password }) {
  try {
    const { user, token, error, permissions, options } = yield call(
      login,
      email,
      password,
    );
    if (token) {
      const counters = yield call(getMenuCounters, token) || {};
      const { locale } = yield select(({ i18n }) => i18n);

      if (user.lang && locale != user.lang) {
        const { translations } = yield call(getTranslations, user.lang);
        if (translations) {
          yield put({ type: I18N.LOAD_DONE, translations });
          yield put({ type: I18N.CHANGE_LOCALE_DONE, locale: user.lang });
        }
      }

      yield put({
        type: USER.LOGIN_DONE,
        user: { ...user, token, permissions, options, counters },
      });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message =
        error && (getErrorMessageKey(translations, error.code) || error.msg);
      yield put({ type: USER.LOGIN_FAILED, message: message || 'login error' });
    }
  } catch (message) {
    yield put({ type: USER.LOGIN_FAILED, message });
  }
}

function* sagaAdd({ fields }) {
  try {
    const { user, id, email_from, error } = yield call(register, fields);
    if (!error) {
      yield put({ type: USER.ADD_DONE, user: { ...user, id, email_from } });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.ADD_FAILED,
        message: message || 'registration error',
      });
    }
  } catch (message) {
    yield put({ type: USER.ADD_FAILED, message });
  }
}

function* sagaLogout() {
  try {
    const { error } = yield call(logout);
    // TODO: parse response when it will work on backend
    if (!error) {
      yield put({ type: USER.LOGOUT_DONE });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.LOGOUT_FAILED,
        message: message || 'logout error',
      });
    }
  } catch (message) {
    yield put({ type: USER.LOGOUT_FAILED, message });
  }
}

function* sagaResetPassword({ email }) {
  try {
    const { error } = yield call(resetPassword, email);
    // TODO: parse response when it will work on backend
    if (!error) {
      // NOTE: "password_resetted_successfully" used in view to indentificate success response
      yield put({
        type: USER.LOGIN_FAILED,
        message: 'password_resetted_successfully',
      });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.LOGIN_FAILED,
        message: message || 'reset password error',
      });
    }
  } catch (message) {
    yield put({ type: USER.LOGIN_FAILED, message });
  }
  // TODO: use "RESET_PASSWORD_DONE" / "RESET_PASSWORD_FAILED" instead of "LOGIN_FAILED"
}

function* sagaGetMe() {
  try {
    const user = yield call(getMe);
    if (!user.error) {
      yield put({ type: USER.GET_DONE, user });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message =
        getErrorMessageKey(translations, user.error.code) || user.error.msg;
      yield put({ type: USER.GET_FAILED, message: message || 'get error' });
    }
  } catch (message) {
    yield put({ type: USER.GET_FAILED, message });
  }
}

function* sagaUpdateMe({ fields }) {
  try {
    const { error } = yield call(updateMe, fields);
    if (!error) {
      yield put({ type: USER.UPDATE_DONE, fields });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.UPDATE_FAILED,
        message: message || 'update error',
      });
    }
  } catch (message) {
    yield put({ type: USER.UPDATE_FAILED, message });
  }
}

function* sagaUploadUserImage({ id, file }) {
  try {
    const res = yield call(uploadUserImage, file, id);
    if (!res.error) {
      yield put({
        type: USER.UPLOAD_DONE,
        image: res.link,
        id,
        storage_id: res.id,
        original_name: res.original_name,
      });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message =
        getErrorMessageKey(translations, res.error.code) || res.error.msg;
      yield put({
        type: USER.UPLOAD_FAILED,
        message: message || 'upload file error',
      });
    }
  } catch (message) {
    yield put({ type: USER.UPLOAD_FAILED, message });
  }
}

function* sagaRemoveUserImage({ storageId, userId }) {
  try {
    const { error } = yield call(removeUserImage, storageId, userId);
    if (!error) {
      yield put({
        type: USER.REMOVE_UPLOADED_DONE,
        image: null,
        id: userId,
        storage_id: null,
        original_name: null,
      });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.REMOVE_UPLOADED_FAILED,
        message: message || 'remove file error',
      });
    }
  } catch (message) {
    yield put({ type: USER.REMOVE_UPLOADED_FAILED, message });
  }
}

function* sagaGetSettings() {
  try {
    const config = yield call(getUserSettings);
    if (!config.error) {
      yield put({ type: USER.GET_SETTINGS_DONE, config });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message =
        getErrorMessageKey(translations, config.error.code) || config.error.msg;
      yield put({
        type: USER.GET_SETTINGS_FAILED,
        message: message || 'get settings error',
      });
    }
  } catch (message) {
    yield put({ type: USER.GET_SETTINGS_FAILED, message });
  }
}

function* sagaUpdateSettings({ fields }) {
  try {
    const { error } = yield call(updateUserSettings, fields);
    if (!error) {
      yield put({ type: USER.UPDATE_SETTINGS_DONE, fields });
    } else {
      const { translations } = yield select(({ i18n }) => i18n);
      const message = getErrorMessageKey(translations, error.code) || error.msg;
      yield put({
        type: USER.UPDATE_SETTINGS_FAILED,
        message: message || 'update settings error',
      });
    }
  } catch (message) {
    yield put({ type: USER.UPDATE_SETTINGS_FAILED, message });
  }
}

export default function* () {
  yield [
    fork(function* () {
      yield takeLatest(USER.LOGIN, sagaLogin);
    }),
    fork(function* () {
      yield takeLatest(USER.LOGOUT, sagaLogout);
    }),
    fork(function* () {
      yield takeLatest(USER.RESET_PASSWORD, sagaResetPassword);
    }),
    fork(function* () {
      yield takeLatest(USER.GET, sagaGetMe);
    }),
    fork(function* () {
      yield takeLatest(USER.ADD, sagaAdd);
    }),
    fork(function* () {
      yield takeLatest(USER.UPDATE, sagaUpdateMe);
    }),
    fork(function* () {
      yield takeLatest(USER.UPLOAD, sagaUploadUserImage);
    }),
    fork(function* () {
      yield takeLatest(USER.REMOVE_UPLOADED, sagaRemoveUserImage);
    }),
    fork(function* () {
      yield takeLatest(USER.GET_SETTINGS, sagaGetSettings);
    }),
    fork(function* () {
      yield takeLatest(USER.UPDATE_SETTINGS, sagaUpdateSettings);
    }),
  ];
}



// WEBPACK FOOTER //
// ./src/sagas/user.js